import { useCallback, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CalendarLine from '@/assets/icons/calendar_line.svg?component';
import { Button } from '@/components/atoms/Buttons';
import { Spinner } from '@/components/atoms/Spinner';
import StyledSingleDatePicker from '@/components/atoms/StyledSingleDatePicker/StyledSingleDatePicker';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import { Menu, MenuContent, MenuTrigger } from '@/components/molecules/Menu';
import { formatDateDisplay, toDateString } from '@/utils/date';

import { type AppliedRange, presetRange, resolveAppliedRange } from './appliedRange';
import {
    DATE_RANGE_PRESETS,
    type DateRangePreset,
    type DateRangePresetId,
} from './DateRangePresets';

import styles from './DateRangePicker.module.scss';

const NO_HIDDEN_PRESETS: readonly DateRangePresetId[] = [];

export interface DateRange {
    dateFrom?: string;
    dateTo?: string;
}

/**
 * How the range was chosen: a preset or Reset always means whole days, while a Confirm of
 * the custom days may leave a day as it was, which a caller holding a finer bound can keep.
 */
export interface DateRangeChange {
    source: 'preset' | 'custom' | 'reset';
}

export interface DateRangePickerProps {
    dateFrom?: string;
    dateTo?: string;
    disabled?: boolean;
    defaultPresetId?: DateRangePresetId;
    menuPosition?: 'left' | 'right';
    show24hPreset?: boolean;
    /** Off by default, like `show24hPreset`. The bioscope depth card turns it on. */
    showLast3DaysPreset?: boolean;
    /** Presets left out of the menu. A range that matches one is labeled by its days. */
    hiddenPresetIds?: readonly DateRangePresetId[];
    onDateChange?: (data: DateRange, change: DateRangeChange) => void;
}

/** A range an action applied while the page passed none, valid for those props only. */
interface LocalRange {
    propsKey: string;
    range: AppliedRange;
}

/** Whether `day` is on a later day than `other`; false while either is missing. */
function isLaterDay(day: Date | null, other: Date | null): boolean {
    return day !== null && other !== null && toDateString(day) > toDateString(other);
}

export default function DateRangePicker(props: DateRangePickerProps) {
    const {
        dateFrom,
        dateTo,
        disabled = false,
        defaultPresetId = 'last4weeks',
        menuPosition = 'left',
        show24hPreset = false,
        showLast3DaysPreset = false,
        hiddenPresetIds,
        onDateChange,
    } = props;
    const { t } = useTranslation();
    // A page whose default is 24h keeps that label even where the preset list hides it.
    const include24h = show24hPreset || defaultPresetId === '24h';
    const includeLast3Days = showLast3DaysPreset || defaultPresetId === 'last3days';
    const hiddenPresets = hiddenPresetIds ?? NO_HIDDEN_PRESETS;

    const [isOpen, setIsOpen] = useState(false);
    const [tempFromDate, setTempFromDate] = useState<Date | null>(null);
    const [tempToDate, setTempToDate] = useState<Date | null>(null);
    const [local, setLocal] = useState<LocalRange | null>(null);
    // useId's colons are valid in an id but not in a CSS selector, and tests select by id.
    const fromDateId = useId().replace(/:/g, '');
    const toDateId = useId().replace(/:/g, '');

    const pageRange = useMemo(
        () =>
            resolveAppliedRange(dateFrom, dateTo, {
                defaultPresetId,
                include24h,
                includeLast3Days,
                hiddenPresetIds: hiddenPresets,
            }),
        [dateFrom, dateTo, defaultPresetId, include24h, includeLast3Days, hiddenPresets]
    );

    // A page that passes a range owns it: the toggle shows what the page applies, also
    // when it refuses a range or has nothing new to send back. A range an action applies
    // itself only stands in while the page passes none, and lapses when the props change.
    const propsKey = `${dateFrom ?? ''}|${dateTo ?? ''}|${defaultPresetId}`;
    if (local !== null && local.propsKey !== propsKey) {
        setLocal(null);
    }
    const localRange = local?.propsKey === propsKey ? local.range : null;
    const applied = pageRange.fromPage ? pageRange : (localRange ?? pageRange);
    const isLoadingState = applied.presetId === 'none';

    const handleOpenChange = useCallback(
        (open: boolean) => {
            if (open) {
                // Synced on open only, so a refetch while the menu is open keeps the typed days.
                setTempFromDate(applied.from);
                setTempToDate(applied.to);
            }
            setIsOpen(open);
        },
        [applied]
    );

    // Editing the days changes nothing until Confirm: clearing the preset here left a
    // "Show all" picker with no preset and no dates, which reads as still loading.
    // A day past the other one clears it. Bounding each day by the other refused the typed
    // day but kept its text, so both inputs could read a range Confirm would not take.
    const handleFromDateChange = useCallback((date: Date | null) => {
        setTempFromDate(date);
        setTempToDate((to) => (isLaterDay(date, to) ? null : to));
    }, []);

    const handleToDateChange = useCallback((date: Date | null) => {
        setTempToDate(date);
        setTempFromDate((from) => (isLaterDay(from, date) ? null : from));
    }, []);

    const commit = useCallback(
        (range: DateRange, change: DateRangeChange) => {
            setLocal({
                propsKey,
                range: resolveAppliedRange(range.dateFrom, range.dateTo, {
                    defaultPresetId,
                    include24h,
                    includeLast3Days,
                    hiddenPresetIds: hiddenPresets,
                }),
            });
            setIsOpen(false);
            onDateChange?.(range, change);
        },
        [propsKey, defaultPresetId, include24h, includeLast3Days, hiddenPresets, onDateChange]
    );

    // Confirm needs both days. With none the toggle fell into its loading state,
    // disabled, with Reset out of reach inside the closed menu; with one, pages that
    // fill a missing side from their default preset replaced the choice anyway.
    const canConfirm = tempFromDate !== null && tempToDate !== null;

    const handleConfirm = useCallback(() => {
        if (tempFromDate === null || tempToDate === null) return;
        commit(
            { dateFrom: toDateString(tempFromDate), dateTo: toDateString(tempToDate) },
            { source: 'custom' }
        );
    }, [tempFromDate, tempToDate, commit]);

    const handlePresetClick = useCallback(
        (preset: DateRangePreset) => commit(presetRange(preset), { source: 'preset' }),
        [commit]
    );

    const handleResetFilters = useCallback(() => {
        const defaultPreset = DATE_RANGE_PRESETS.find((p) => p.id === defaultPresetId);
        if (defaultPreset && defaultPreset.id !== 'none') {
            commit(presetRange(defaultPreset), { source: 'reset' });
            return;
        }
        // `none` leaves the default range to the page, so Reset hands the choice back and
        // keeps showing what the page passes. Waiting for new props in the loading state
        // hung for good on a page that already showed its default and sent nothing new.
        setLocal(null);
        setIsOpen(false);
        onDateChange?.({ dateFrom: undefined, dateTo: undefined }, { source: 'reset' });
    }, [defaultPresetId, commit, onDateChange]);

    const appliedPreset = DATE_RANGE_PRESETS.find((p) => p.id === applied.presetId);
    const buttonText = appliedPreset
        ? t(appliedPreset.labelKey)
        : `${formatDateDisplay(applied.from)} - ${formatDateDisplay(applied.to)}`.trim();

    return (
        <div className={styles.dateRangePicker}>
            <Menu kind="panel" isOpen={isOpen} onOpenChange={handleOpenChange} disabled={disabled}>
                <MenuTrigger
                    variant="secondary"
                    data-testid="date-range-picker-toggle"
                    disabled={disabled}
                    className={styles.toggleButton}
                    icon={<SvgIcon icon={CalendarLine} size="medium" color="currentColor" />}
                    caret>
                    {isLoadingState ? (
                        <span className={styles.spinnerContainer}>
                            <Spinner className={styles.spinner} />
                        </span>
                    ) : (
                        <span className={styles.buttonText}>{buttonText}</span>
                    )}
                </MenuTrigger>

                <MenuContent
                    className={styles.dropdownMenu}
                    align={menuPosition === 'right' ? 'end' : 'start'}>
                    <div className={styles.section}>
                        <div className={styles.title}>{t('molecules.dateRangePicker.title')}</div>

                        <div className={styles.datePickersContainer}>
                            <div className={styles.datePickerGroup}>
                                <label className={styles.label} htmlFor={fromDateId}>
                                    {t('molecules.dateRangePicker.fromDate')}
                                </label>
                                <StyledSingleDatePicker
                                    id={fromDateId}
                                    date={tempFromDate}
                                    onDateChange={handleFromDateChange}
                                />
                            </div>

                            <div className={styles.datePickerGroup}>
                                <label className={styles.label} htmlFor={toDateId}>
                                    {t('molecules.dateRangePicker.toDate')}
                                </label>
                                <StyledSingleDatePicker
                                    id={toDateId}
                                    date={tempToDate}
                                    onDateChange={handleToDateChange}
                                />
                            </div>
                        </div>

                        <div className={styles.buttonGroup}>
                            <Button
                                variant="secondary"
                                type="button"
                                className={styles.resetButton}
                                onClick={handleResetFilters}>
                                {t('molecules.dateRangePicker.resetFilters')}
                            </Button>
                            <Button
                                variant="primary"
                                type="button"
                                className={styles.confirmButton}
                                disabled={!canConfirm}
                                onClick={handleConfirm}>
                                {t('molecules.dateRangePicker.confirm')}
                            </Button>
                        </div>
                    </div>

                    <div className={styles.section}>
                        <div className={styles.title}>
                            {t('molecules.dateRangePicker.presetsTitle')}
                        </div>

                        <div className={styles.presetsContainer}>
                            {DATE_RANGE_PRESETS.filter((preset) => {
                                // Filter out 24h preset if show24hPreset is false
                                if (preset.id === '24h' && !show24hPreset) {
                                    return false;
                                }
                                if (preset.id === 'last3days' && !showLast3DaysPreset) {
                                    return false;
                                }
                                if (hiddenPresets.includes(preset.id)) {
                                    return false;
                                }
                                // Filter out 'none' preset as it's only for internal loading state
                                if (preset.id === 'none') {
                                    return false;
                                }
                                return true;
                            }).map((preset) => (
                                <button
                                    key={preset.id}
                                    type="button"
                                    className={`${styles.presetItem} ${
                                        applied.presetId === preset.id ? styles.selected : ''
                                    }`}
                                    onClick={() => handlePresetClick(preset)}>
                                    <span className={styles.presetLabel}>{t(preset.labelKey)}</span>
                                    <span className={styles.presetDateRange}>
                                        {preset.getDateRangeDisplay()}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>
                </MenuContent>
            </Menu>
        </div>
    );
}
